import { getMeLatestUserSubscriptions } from '@api/selectors/subscription';
import { subscriptionTypes } from '@constants/index';
import { NOT_APPLICABLE } from '@constants/segment';
import { getStore } from 'src/redux';
import { getPrimeQuotaForMode } from './quotaHelpers';
import usePrimeQuotaStore from './store';
import { PrimeMode, UserPlatformPlan } from './types';

const PRIME_CONTENT_TYPE_ID = 507;
const SECONDS_IN_DAY = 3600 * 24;

type UserSubscription = {
  contentTypeId?: number;
  content_type_id?: number;
  timeRemaining?: number;
  time_remaining?: number;
  subscription?: {
    type?: number;
    contentTypeId?: number;
    content_type_id?: number;
  };
};

const getActivePrimeSubscription = (): UserSubscription | null => {
  if (typeof window === 'undefined') return null;

  try {
    const me = getStore().getState()?.auth?.me;
    if (!me) return null;

    const subscriptions = getMeLatestUserSubscriptions(
      me
    ) as UserSubscription[];

    return (
      subscriptions.find((subscription) => {
        const contentTypeId =
          subscription?.contentTypeId ??
          subscription?.content_type_id ??
          subscription?.subscription?.contentTypeId ??
          subscription?.subscription?.content_type_id;
        const timeRemaining =
          subscription?.time_remaining ?? subscription?.timeRemaining ?? 0;

        return (
          Number(contentTypeId) === PRIME_CONTENT_TYPE_ID && timeRemaining > 0
        );
      }) ?? null
    );
  } catch {
    return null;
  }
};

const getUnaProGlobalProps = () => ({
  una_pro_subscription_plan: NOT_APPLICABLE,
  days_left_in_una_pro_subscription_plan: NOT_APPLICABLE,
  daily_talktime_available: NOT_APPLICABLE,
  daily_messages_available: NOT_APPLICABLE,
  monthly_talktime_available: NOT_APPLICABLE,
  monthly_messages_available: NOT_APPLICABLE,
  target_goal: NOT_APPLICABLE,
  selected_language: NOT_APPLICABLE
});

const getPrimeSubscriptionPlanName = ({
  userPlatformPlan,
  activePrimeSubscription
}: {
  userPlatformPlan: UserPlatformPlan | null;
  activePrimeSubscription: UserSubscription | null;
}) => {
  if (userPlatformPlan === UserPlatformPlan.FREE) {
    return 'Free';
  }

  if (userPlatformPlan === UserPlatformPlan.PAID) {
    if (
      activePrimeSubscription?.subscription?.type === subscriptionTypes.TRIAL
    ) {
      return 'Trial';
    }
    return 'Paid';
  }

  if (activePrimeSubscription) {
    if (
      activePrimeSubscription.subscription?.type === subscriptionTypes.TRIAL
    ) {
      return 'Trial';
    }
    return 'Paid';
  }

  return NOT_APPLICABLE;
};

const getDaysLeftInPrimeSubscription = ({
  userPlatformPlan,
  activePrimeSubscription
}: {
  userPlatformPlan: UserPlatformPlan | null;
  activePrimeSubscription: UserSubscription | null;
}) => {
  if (userPlatformPlan === UserPlatformPlan.FREE) {
    return NOT_APPLICABLE;
  }

  const timeRemaining =
    activePrimeSubscription?.time_remaining ??
    activePrimeSubscription?.timeRemaining;

  if (!timeRemaining || timeRemaining <= 0) {
    return NOT_APPLICABLE;
  }

  return Math.ceil(timeRemaining / SECONDS_IN_DAY);
};

const getPrimeGlobalProps = () => {
  const { quotaDetails, userPlatformPlan } = usePrimeQuotaStore.getState();
  const activePrimeSubscription = getActivePrimeSubscription();

  const videoQuota = getPrimeQuotaForMode({
    mode: PrimeMode.VIDEO,
    quotaDetails,
    userPlatformPlan
  });
  const practiceQuota = getPrimeQuotaForMode({
    mode: PrimeMode.PRACTICE,
    quotaDetails,
    userPlatformPlan
  });
  const testQuota = getPrimeQuotaForMode({
    mode: PrimeMode.TEST,
    quotaDetails,
    userPlatformPlan
  });

  const isFreePlan = userPlatformPlan === UserPlatformPlan.FREE;

  return {
    ...getUnaProGlobalProps(),
    current_prime_subscription_plan: getPrimeSubscriptionPlanName({
      userPlatformPlan,
      activePrimeSubscription
    }),
    days_left_in_prime_subscription: getDaysLeftInPrimeSubscription({
      userPlatformPlan,
      activePrimeSubscription
    }),
    daily_prime_videos_available: isFreePlan ? videoQuota.left : NOT_APPLICABLE,
    daily_prime_practice_available: isFreePlan
      ? practiceQuota.left
      : NOT_APPLICABLE,
    daily_prime_tests_available: isFreePlan ? testQuota.left : NOT_APPLICABLE
  };
};

export default getPrimeGlobalProps;
